import { IKey } from '@/common/constants/ikey'
import { normalizePageTabId } from '@/common/composables/useFormStates'
import type { ActionContext, ActionDependencies } from '@/services/action/types'
import {
  branchSetsTreeTableSearchFlag,
  extractLiteralRouteFromPageScript,
  extractTabRouteFieldFromScript,
  findTabRoutedSearchBranch,
  pageScriptSetsRouteViaTypesJoin,
  parseTabRoutedSearchBranches,
  type TabRoutedSearchBranch,
} from '@/services/action/parseTabRoutedSearchOnClick'

function readFieldEntry(pageFields: Record<string, unknown> | undefined, fieldName: string): unknown {
  if (!pageFields) return undefined
  const entry = pageFields[fieldName]
  if (entry == null) return undefined
  if (typeof entry === 'object' && entry !== null && 'value' in entry) {
    return (entry as { value: unknown }).value
  }
  return entry
}

export function readStringField(
  pageFields: Record<string, unknown> | undefined,
  fieldName: string,
): string {
  const raw = readFieldEntry(pageFields, fieldName)
  if (raw == null) return ''
  if (typeof raw === 'string') return raw.trim()
  return String(raw).trim()
}

function isTruthyField(pageFields: Record<string, unknown> | undefined, fieldName: string): boolean {
  const raw = readFieldEntry(pageFields, fieldName)
  if (raw === true || raw === 1) return true
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase()
    return s === 'true' || s === '1'
  }
  return false
}

function normalizePageStates(states: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(states)) return states as Array<Record<string, unknown>>
  if (states && typeof states === 'object') return Object.values(states) as Array<Record<string, unknown>>
  return []
}

export function getFocusedPageId(pageData?: Record<string, unknown> | null): string {
  const jsResult = pageData?.jsResult as { states?: unknown } | undefined
  const states = normalizePageStates(jsResult?.states)
  for (let i = states.length - 1; i >= 0; i -= 1) {
    const state = states[i]
    if (String(state?.element_level ?? '').toUpperCase() !== 'PAGE') continue
    if (state?.is_visible === false) continue
    if (state?.is_focused !== true) continue
    if (state?.element_id == null) continue
    return normalizePageTabId(state.element_id)
  }
  return ''
}

function mergeRouteResolutionFields(
  pageFields: Record<string, unknown>,
  mergedFields?: Record<string, unknown>,
): Record<string, unknown> {
  if (!mergedFields || Object.keys(mergedFields).length === 0) return { ...pageFields }
  return { ...pageFields, ...mergedFields }
}

function readPageOnClickScript(page: Record<string, unknown>): string {
  const onClick = page.onClick
  return String((onClick as { _text?: unknown })?._text ?? onClick ?? '').trim()
}

function visitFormPages(
  pageData: Record<string, unknown> | null | undefined,
  visitor: (pageId: string, onClickScript: string) => void | 'stop',
): void {
  if (!pageData?.form) return
  const visit = (node: unknown): boolean => {
    if (!node) return false
    if (Array.isArray(node)) {
      for (const item of node) {
        if (visit(item)) return true
      }
      return false
    }
    if (typeof node !== 'object') return false
    const page = node as { id?: string | number; onClick?: unknown; _attributes?: { id?: string | number } }
    const id = normalizePageTabId(page.id ?? page._attributes?.id)
    const script = readPageOnClickScript(page as Record<string, unknown>)
    if (id && script && visitor(id, script) === 'stop') return true
    for (const value of Object.values(page as Record<string, unknown>)) {
      if (visit(value)) return true
    }
    return false
  }
  visit(pageData.form)
}

function findPageOnClickScript(pageData: Record<string, unknown> | null | undefined, pageId: string): string {
  if (!pageId) return ''
  const wanted = normalizePageTabId(pageId)
  let found = ''
  visitFormPages(pageData, (id, script) => {
    if (id === wanted) {
      found = script
      return 'stop'
    }
  })
  return found
}

function findPageOnClickByLoadedBase(
  pageData: Record<string, unknown> | null | undefined,
  loadedBase: string,
): string {
  const loadedFlag = `${loadedBase}-loaded`
  let found = ''
  visitFormPages(pageData, (_id, script) => {
    if (!script.includes(loadedFlag)) return
    found = script
    return 'stop'
  })
  return found
}

function readPrimitiveFieldValue(raw: unknown): string {
  if (raw == null) return ''
  if (typeof raw === 'string') return raw
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    return String((raw as { value?: unknown }).value ?? '')
  }
  return String(raw)
}

function inferRouteFromTypesJoinPageScript(pageOnClick: string, selectableTypeIdsRaw: unknown): string {
  const selectable = readPrimitiveFieldValue(selectableTypeIdsRaw)
  const types: string[] = []
  const re = /types\.push\s*\(\s*["']([^'"]+)["']\s*\)/g
  let match: RegExpExecArray | null
  while ((match = re.exec(pageOnClick))) {
    const typeName = match[1] ?? ''
    if (!typeName) continue
    if (selectable.includes(typeName)) types.push(typeName)
  }
  return types.join('+')
}

function resolveRouteFromPageScript(
  pageOnClick: string,
  routeField: string,
  selectableTypeIdsRaw: unknown,
): string {
  if (!pageOnClick.trim() || !routeField) return ''
  const literal = extractLiteralRouteFromPageScript(pageOnClick, routeField)
  if (literal) return literal
  if (pageScriptSetsRouteViaTypesJoin(pageOnClick, routeField)) {
    return inferRouteFromTypesJoinPageScript(pageOnClick, selectableTypeIdsRaw)
  }
  return ''
}

function collectLoadedRouteBases(fields: Record<string, unknown>): string[] {
  const bases: string[] = []
  for (const [key] of Object.entries(fields)) {
    if (!key.endsWith('-loaded')) continue
    if (!isTruthyField(fields, key)) continue
    const candidate = key.slice(0, -'-loaded'.length)
    if (candidate) bases.push(candidate)
  }
  return bases
}

function mapLoadedFlagToRouteValue(
  loadedBase: string,
  routeField: string,
  pageData: Record<string, unknown> | null | undefined,
  merged: Record<string, unknown>,
): string {
  const pageOnClick = findPageOnClickByLoadedBase(pageData, loadedBase)
  if (pageOnClick) {
    const resolved = resolveRouteFromPageScript(
      pageOnClick,
      routeField,
      readFieldEntry(merged, 'selectableTypeIds'),
    )
    if (resolved) return resolved
  }
  return loadedBase
}

function loadedBaseMatchesRouteValue(
  loadedBase: string,
  routeValue: string,
  routeField: string,
  pageData: Record<string, unknown> | null | undefined,
  fields: Record<string, unknown>,
): boolean {
  if (!loadedBase || !routeValue) return false
  const mapped = mapLoadedFlagToRouteValue(loadedBase, routeField, pageData, fields)
  if (mapped === routeValue) return true
  if (routeValue.split('+').includes(loadedBase)) return true
  return false
}

function resolveRouteFromPageId(
  pageId: string,
  routeField: string,
  pageData: Record<string, unknown> | null | undefined,
  merged: Record<string, unknown>,
): string {
  if (!pageId || !routeField) return ''
  const pageOnClick = findPageOnClickScript(pageData, pageId)
  return resolveRouteFromPageScript(pageOnClick, routeField, readFieldEntry(merged, 'selectableTypeIds'))
}

function resolveElementFieldNameByRef(
  elementRef: string,
  dependencies?: ActionDependencies,
): string {
  const getElements = dependencies?.formProvider?.getElements
  if (typeof getElements !== 'function') return ''
  const elements = getElements()
  for (const el of Object.values(elements)) {
    if (el == null || typeof el !== 'object') continue
    const rec = el as { elementId?: string | number; fieldName?: string; name?: string }
    if (String(rec.elementId ?? '') === elementRef) {
      return String(rec.fieldName ?? rec.name ?? '').trim()
    }
  }
  return ''
}

function resolveTableNameFromBranch(
  branch: TabRoutedSearchBranch,
  dependencies?: ActionDependencies,
): string {
  if (branch.reloadElementId) {
    const fromRef = resolveElementFieldNameByRef(branch.reloadElementId, dependencies)
    if (fromRef) return fromRef
  }
  if (branch.reloadElementName) return branch.reloadElementName
  for (const field of branch.setFields) {
    const m = field.fieldName.match(/^__element__:(.+):(.+)$/)
    if (!m) continue
    const elementRef = String(m[1] ?? '').trim()
    const suffix = m[2] ?? ''
    const base = resolveElementFieldNameByRef(elementRef, dependencies)
    if (base && suffix) return base
  }
  return ''
}

function tableHasSearchActivity(fields: Record<string, unknown>, tableName: string): boolean {
  if (!tableName) return false
  if (isTruthyField(fields, `${tableName}${IKey.TT_FORCE_LOAD_ALL}`)) return true
  if (isTruthyField(fields, `${tableName}__search`)) return true
  if (isTruthyField(fields, `${tableName}${IKey.TT_NEXT_SEARCH_POSITION}`)) return true
  const tableField = readFieldEntry(fields, tableName)
  if (tableField && typeof tableField === 'object' && tableField !== null && 'value' in tableField) {
    const rows = (tableField as { value?: { rows?: unknown[] } }).value?.rows
    if (Array.isArray(rows) && rows.length > 0) return true
  }
  return false
}

function inferRouteFromSearchScriptBranches(
  script: string,
  routeField: string,
  fields: Record<string, unknown>,
  loadedBases: string[],
  pageData: Record<string, unknown> | null | undefined,
  dependencies?: ActionDependencies,
): string {
  if (!script.trim() || loadedBases.length === 0 || !routeField) return ''
  for (const branch of parseTabRoutedSearchBranches(script)) {
    if (!branchSetsTreeTableSearchFlag(branch)) continue
    const tableName = resolveTableNameFromBranch(branch, dependencies)
    if (!tableName || !tableHasSearchActivity(fields, tableName)) continue
    for (const routeValue of branch.routeValues) {
      for (const base of loadedBases) {
        if (loadedBaseMatchesRouteValue(base, routeValue, routeField, pageData, fields)) return routeValue
        if (mapLoadedFlagToRouteValue(base, routeField, pageData, fields) === routeValue) return routeValue
      }
    }
  }
  return ''
}

function resolveRouteFromLoadedFlags(
  fields: Record<string, unknown>,
  routeField: string,
  activePageId: string,
  pageData: Record<string, unknown> | null | undefined,
  actionScript: string,
  dependencies?: ActionDependencies,
): string {
  const loadedBases = collectLoadedRouteBases(fields)
  if (loadedBases.length === 0) return ''
  if (loadedBases.length === 1) {
    return loadedBases[0]
  }

  const fromBranches = inferRouteFromSearchScriptBranches(
    actionScript,
    routeField,
    fields,
    loadedBases,
    pageData,
    dependencies,
  )
  if (fromBranches) {
    for (const base of loadedBases) {
      if (mapLoadedFlagToRouteValue(base, routeField, pageData, fields) === fromBranches) return base
      if (loadedBaseMatchesRouteValue(base, fromBranches, routeField, pageData, fields)) return base
    }
  }

  if (activePageId) {
    const pageRoute = resolveRouteFromPageId(activePageId, routeField, pageData, fields)
    if (pageRoute) {
      for (const base of loadedBases) {
        if (loadedBaseMatchesRouteValue(base, pageRoute, routeField, pageData, fields)) {
          return base
        }
      }
    }
  }

  const simpleLoaded = loadedBases.filter(
    (base) => !mapLoadedFlagToRouteValue(base, routeField, pageData, fields).includes('+'),
  )
  if (simpleLoaded.length === 1) {
    return simpleLoaded[0]
  }

  return loadedBases[loadedBases.length - 1]
}

export function getActionOnClickScript(context: ActionContext): string {
  if (typeof context.onClick === 'string' && context.onClick.trim()) return context.onClick.trim()
  const fromData = context.data?.onClick
  return String(fromData?._text ?? fromData ?? '').trim()
}

export function resolveTabRouteFieldFromContext(context: ActionContext): string {
  return extractTabRouteFieldFromScript(getActionOnClickScript(context)) ?? ''
}

export function resolveTabRouteValue(
  pageFields: Record<string, unknown>,
  context: ActionContext,
  pageData?: Record<string, unknown> | null,
  mergedFields?: Record<string, unknown>,
  dependencies?: ActionDependencies,
): string {
  const actionScript = getActionOnClickScript(context)
  const routeField = extractTabRouteFieldFromScript(actionScript)
  if (!routeField) return ''

  const merged = mergeRouteResolutionFields(pageFields, mergedFields)
  const focusedPageId = getFocusedPageId(pageData) || String(context.pageId ?? '').trim()
  const storedRoute = readStringField(merged, routeField)

  if (storedRoute && isTruthyField(merged, `${storedRoute}-loaded`)) {
    return storedRoute
  }

  if (mergedFields && Object.keys(mergedFields).length > 0 && storedRoute) {
    const pageStoredRoute = readStringField(pageFields, routeField)
    if (storedRoute !== pageStoredRoute) {
      for (const base of collectLoadedRouteBases(merged)) {
        const mapped = mapLoadedFlagToRouteValue(base, routeField, pageData ?? null, merged)
        if (
          mapped === storedRoute ||
          loadedBaseMatchesRouteValue(base, storedRoute, routeField, pageData ?? null, merged)
        ) {
          return storedRoute
        }
      }
    }
  }

  const fromLoaded = resolveRouteFromLoadedFlags(
    merged,
    routeField,
    focusedPageId,
    pageData ?? null,
    actionScript,
    dependencies,
  )
  if (fromLoaded) {
    return mapLoadedFlagToRouteValue(fromLoaded, routeField, pageData ?? null, merged)
  }

  if (focusedPageId) {
    const fromFocused = resolveRouteFromPageId(focusedPageId, routeField, pageData ?? null, merged)
    if (fromFocused) return fromFocused
  }

  return storedRoute
}

export function resolveTreeTableNameForTabRouteBranch(
  script: string,
  routeValue: string,
  dependencies?: ActionDependencies,
): string | null {
  const branch = findTabRoutedSearchBranch(script, routeValue)
  if (!branch || !branchSetsTreeTableSearchFlag(branch)) return null
  const name = resolveTableNameFromBranch(branch, dependencies)
  return name || null
}
